// Source-of-truth list of every gateable Teamlio panel/page.
//
// `key` is a stable, app-independent identifier used as the permission key in
// an Access profile's `permissions` JSON. Messenger ships an identical catalog
// (same keys) so a single Access controls both apps. `teamlioPath` is the route
// this panel lives at in *this* app — used to gate the sidebar and routes.

export type PanelLevel = "view" | "manage";
export const BLOG_MANAGEMENT_HEAD_OFFICE_ID = "9962e696-9b36-4227-8918-9ed42d6cf32c";

export interface PanelDef {
  key: string;
  label: string;
  group: string;
  teamlioPath: string;
  // A "feature" is a sub-permission inside a panel (e.g. an employee-form tab),
  // not a routed page. It has no teamlioPath, never gates the sidebar/routes,
  // and defaults to granted (visible:true) unless explicitly turned off.
  feature?: boolean;
  // For features: the panel key they nest under. They render indented beneath
  // that panel's row and only when the parent is enabled at "manage" level.
  parent?: string;
  // A "leveled" feature carries view/manage semantics (not just on/off). The
  // Access grid renders a View/Manage toggle for it instead of Allowed/Hidden,
  // and `canManage(key)` is meaningful. Used by the Leaves sub-tabs.
  leveled?: boolean;
  // Default cell used by `blankPermissions` when a profile doesn't yet define
  // this key (e.g. a brand-new profile, or an old one predating the feature).
  // Features otherwise default to visible. Set false to make a feature opt-in.
  defaultVisible?: boolean;
  defaultLevel?: PanelLevel;
  // Force View on for EVERY profile/role, always — the panel can never be hidden
  // (View is locked true for all roles). Admins can still raise it to Manage for
  // edit rights. Used for read-for-all pages like Company Policy.
  forceView?: boolean;
}

export const PANEL_CATALOG: PanelDef[] = [
  { key: "dashboard", label: "Dashboard", group: "Main", teamlioPath: "/dashboard" },
  { key: "employees", label: "Employees", group: "Main", teamlioPath: "/employees" },
  { key: "attendance", label: "Attendance", group: "Main", teamlioPath: "/attendance" },
  // Face check-in (camera verification above Clock In). Opt-in per person:
  // default hidden and in NO hrms role-seed matrix, so it stays off for
  // everyone until an admin grants it — biometrics are the one feature where
  // "on by default, opt out later" is the wrong way round.
  //
  // Top-level with an empty teamlioPath rather than a feature under
  // "attendance", despite being conceptually a sub-permission: the grid only
  // renders a child row when its parent is Visible AND at Manage
  // (PermissionGridEditor), and the seeded Employee profile has attendance at
  // VIEW — so nesting it would hide this toggle on precisely the profiles it
  // exists to be granted to. Empty path is safe: panelKeyForPath skips falsy
  // paths, so it gates no route and no sidebar entry.
  { key: "attendance-face", label: "Face Check-In", group: "Main", teamlioPath: "", defaultVisible: false, defaultLevel: "view" },
  { key: "leaves", label: "Leaves", group: "Main", teamlioPath: "/leaves" },
  // Self-service inbox — default ON for every profile/role so each user can see
  // their OWN generated paperwork. Still toggleable per profile from the Access
  // panel (not forceView), so admins can hide it for a specific role if needed.
  { key: "documents", label: "Documents", group: "Main", teamlioPath: "/documents", defaultVisible: true },
  { key: "downloads", label: "Downloads", group: "Main", teamlioPath: "/downloads" },
  { key: "separation", label: "Separation Management", group: "Main", teamlioPath: "/separation" },
  // Assets: default ON for every profile/role (self-service "My Assets" — every
  // employee can see what's issued to them). Manage level unlocks the org-wide
  // register (add/edit/assign/approve requests). Profiles saved before this
  // panel existed fall back to defaultVisible, so nobody gets locked out.
  { key: "assets", label: "Assets", group: "Main", teamlioPath: "/assets", defaultVisible: true, defaultLevel: "view" },
  // Expenses: default ON for every profile/role so employees can submit and
  // track their own claims. Manage unlocks approval/reimbursement workflows.
  // Profiles saved before this panel existed also use this visible-by-default
  // fallback until an admin explicitly changes the cell in Access Management.
  { key: "expenses", label: "Expenses", group: "Main", teamlioPath: "/expenses", defaultVisible: true, defaultLevel: "view" },
  // Visitors: default ON at VIEW for every profile/role — inviting the people
  // who come to see you is self-service, like "My Assets". Manage is the front
  // desk: the branch-wide register, the QR scanner, check-in/out, and hosting a
  // visit on someone else's behalf. Reception / security are granted Manage
  // against their own role here rather than through a hardcoded role list,
  // since that role is created per org. Profiles saved before this panel
  // existed fall back to defaultVisible, so nobody is locked out of their own
  // invitations.
  { key: "visitors", label: "Visitor Management", group: "Main", teamlioPath: "/visitors", defaultVisible: true, defaultLevel: "view" },
  // Feedback & Complaints: default ON at VIEW for every profile/role — raising
  // and tracking your own submission is self-service, like "My Assets". Manage
  // unlocks the HR review queue (assign / prioritize / respond / resolve /
  // close). Deliberately NOT Manage for managers by default: a complaint is
  // often about a manager, so an admin grants the queue to a specific person in
  // Access Management (or assigns them one ticket, which lets them handle just
  // that one). Profiles saved before this panel existed fall back to
  // defaultVisible, so nobody is locked out of their own submissions.
  { key: "grievances", label: "Feedback & Complaints", group: "Main", teamlioPath: "/grievances", defaultVisible: true, defaultLevel: "view" },
  // Messenger document storage. The nested management permission controls the
  // quota/usage administration tab; ordinary DBMS access remains available to
  // all standard roles by default.
  { key: "dbms", label: "DBMS", group: "Main", teamlioPath: "/dbms", defaultVisible: true, defaultLevel: "manage" },
  { key: "dbms-storage-access", label: "Storage Access Management", group: "Main", teamlioPath: "", feature: true, parent: "dbms", leveled: true, defaultVisible: false, defaultLevel: "manage" },
  // AI assistant (Lio). Lives in the Messenger app; gated here so a single
  // Access profile governs it too — the same "leo" key the hrms backend enforces
  // on /leo/*. Opt-in: default hidden, so staff get it only once an admin turns
  // it on. The hrms role seed switches it on for the Manager + Elevated tiers.
  { key: "leo", label: "AI Assistant (Lio)", group: "Main", teamlioPath: "/leo", defaultVisible: false, defaultLevel: "view" },
  // Bulk task-reminder DMs (sent AS Lio, not the caller) reach every selected
  // person's chat at once, so it's its OWN panel rather than folded into "leo"
  // view access or reusing "tasks" — Tasks@manage is the seeded Employee
  // default, and that must not double as permission to message the whole org.
  // Nothing grants this by default; admin/manager have it via role fallback
  // (see hrms leo/tools.py's remind_pending_work), same as "leo" itself.
  { key: "reminders", label: "Task Reminders", group: "Main", teamlioPath: "", feature: true, parent: "leo", leveled: true, defaultVisible: false, defaultLevel: "view" },

  { key: "todo", label: "Daily Planner", group: "Task Management", teamlioPath: "/todo" },
  // Notemaker: personal rich-text notes. Visible for everyone by default
  // (a profile saved before it existed falls back to these flags), same
  // as the planner; the backend (hrms routers/notes.py) only refuses an
  // EXPLICIT visible:false cell.
  { key: "notemaker", label: "Notemaker", group: "Task Management", teamlioPath: "/notes", defaultVisible: true, defaultLevel: "manage" },
  { key: "projects", label: "Projects", group: "Task Management", teamlioPath: "/projects" },
  { key: "meetly", label: "Meetly", group: "Task Management", teamlioPath: "/meetly" },
  { key: "task-reports", label: "Reports", group: "Task Management", teamlioPath: "/tasks/reports" },
  { key: "tasks", label: "Tasks", group: "Task Management", teamlioPath: "/tasks" },
  { key: "clients", label: "Clients", group: "Task Management", teamlioPath: "/clients" },
  { key: "vendors", label: "Vendors", group: "Task Management", teamlioPath: "/vendors" },

  { key: "work-schedule", label: "Work Schedule", group: "Work Monitor", teamlioPath: "/screen-recording" },
  { key: "activity-reports", label: "Activity Reports", group: "Work Monitor", teamlioPath: "/activity-reports" },
  { key: "activity-comparison", label: "Activity Comparison", group: "Work Monitor", teamlioPath: "/activity-comparison" },

  // Penalties: default ON at view so anyone fined can see the fine without an
  // admin having to remember to enable a panel — the register scopes itself to
  // the caller's own rows server-side. Manage unlocks levying and deciding.
  { key: "penalties", label: "Penalties", group: "Payroll", teamlioPath: "/penalties", defaultVisible: true, defaultLevel: "view" },
  // Allowances: self-service, same shape as Expenses.
  { key: "allowances", label: "Allowances", group: "Payroll", teamlioPath: "/allowances", defaultVisible: true, defaultLevel: "view" },
  { key: "payroll-records", label: "Payroll Records", group: "Payroll", teamlioPath: "/payroll-record" },
  { key: "payroll-approval", label: "Payroll Approval", group: "Payroll", teamlioPath: "/approval-settings" },
  { key: "payroll-reports", label: "Reports", group: "Payroll", teamlioPath: "/payroll/reports", defaultVisible: true },

  { key: "set-company", label: "Company", group: "Settings", teamlioPath: "/settings/company" },
  { key: "set-office", label: "Office", group: "Settings", teamlioPath: "/settings/office" },
  { key: "set-department", label: "Department", group: "Settings", teamlioPath: "/settings/department" },
  { key: "set-designation", label: "Designation", group: "Settings", teamlioPath: "/settings/designation" },
  { key: "set-references", label: "References", group: "Settings", teamlioPath: "/settings/references" },
  // Salary & Compliance: retainer-ship defaults + the org-wide retainer liability
  // report. Opt-in like the other admin settings panels — before this key existed
  // the page matched no panel, so `isPathVisible` returned true and ANY user with
  // an Access profile could open it. View = read the defaults/liability report,
  // Manage = edit and save the retainer defaults.
  // Shared attendance kiosks. Default hidden (like the other credential pages):
  // a kiosk credential can mark attendance for everyone on its roster, so it is
  // granted deliberately rather than inherited by whoever happens to be senior.
  { key: "set-attendance-kiosk", label: "Attendance Kiosk", group: "Settings", teamlioPath: "/settings/attendance-kiosk", defaultVisible: false, defaultLevel: "manage" },
  { key: "set-salary-compliance", label: "Salary & Compliance", group: "Settings", teamlioPath: "/settings/salary-compliance" },
  { key: "set-terms", label: "Terms & Conditions", group: "Settings", teamlioPath: "/settings/terms-and-conditions" },
  // Company Policy: View is forced on for every role, always (can't be hidden);
  // admins can still raise a profile to Manage for add/update/delete rights.
  { key: "set-company-policy", label: "Company Policy", group: "Settings", teamlioPath: "/settings/company-policy", forceView: true },
  // Policy Rules: the engine's own configuration (grace periods, late-coming,
  // overtime, leave rules…). NOT forceView — unlike Company Policy this is
  // admin configuration, not staff reading material, so it stays opt-in per
  // profile. Manage unlocks editing (Phase 3); View is read-only.
  { key: "set-policy-rules", label: "Policy Rules", group: "Settings", teamlioPath: "/settings/policy-rules" },
  { key: "set-startupkhata", label: "Startup Khata", group: "Settings", teamlioPath: "/settings/startupkhata" },
  { key: "set-task-settings", label: "Task Settings", group: "Settings", teamlioPath: "/settings/task-code" },
  { key: "set-access-management", label: "Access Management", group: "Settings", teamlioPath: "/settings/access-management" },
  { key: "set-roles", label: "Roles", group: "Settings", teamlioPath: "/settings/roles" },
  { key: "set-announcements", label: "Announcements", group: "Settings", teamlioPath: "/settings/announcements" },
  // Notification routing matrix: who gets told about what (event × recipient
  // relation × channel). Admin configuration, so opt-in per profile like the
  // other Settings panels. View = read the grid, Manage = change the routing.
  { key: "set-notifications", label: "Notifications", group: "Settings", teamlioPath: "/settings/notifications" },
  { key: "set-blog-management", label: "Blog Management", group: "Settings", teamlioPath: "/settings/blog-management", defaultVisible: false, defaultLevel: "manage" },
  // Email (SMTP): the branch's own outgoing-mail account, including its
  // password. It was reachable by HR from two directions at once — the settings
  // menu listed it for every elevated role, and the page matched NO panel key,
  // so `isPathVisible` waved through anyone holding an Access profile. Opt-in
  // and Manage-only: there is no meaningful read-only view of a mail account,
  // and nobody should hold the org's sending credentials by default. Admins have
  // no Access profile, so their route in is the backend's role fallback
  // (routers/email.py), not this cell.
  { key: "set-smtp-settings", label: "Email (SMTP)", group: "Settings", teamlioPath: "/settings/smtp-settings", defaultVisible: false, defaultLevel: "manage" },
  // PF/ESIC Settings: holds the branch's EPFO (and later ESIC) portal login,
  // which the Startup Khata extension replays to auto-fill and file. Same
  // tier as Email (SMTP) and Attendance Kiosk — opt-in, Manage-only.
  // { key: "set-pf-esic-settings", label: "PF/ESIC Settings", group: "Settings", teamlioPath: "/settings/pf-esic-settings", defaultVisible: false, defaultLevel: "manage" },
  // Reads other people's device and IP history, so it is a panel in its own
  // right rather than part of Access Management. Every user reaches their OWN
  // sessions at /active-devices, which is not gated.
  { key: "login_sessions", label: "Login Sessions", group: "Settings", teamlioPath: "/login-sessions" },

  // Employee-form sub-sections (gated inside the Add/Edit Employee form when the
  // user has the Employees panel at Manage). Personal Details & Review are always
  // available — they're required to create/save an employee.
  { key: "emp-salary", label: "Salary & Deductions", group: "Main", teamlioPath: "", feature: true, parent: "employees" },
  { key: "emp-documents", label: "Upload Documents", group: "Main", teamlioPath: "", feature: true, parent: "employees" },

  // Leaves sub-tabs — each tab inside /leaves is gated as a leveled feature.
  // View = can see the tab (read-only); Manage = can act (apply, approve, edit).
  // When a profile doesn't specify one, the Leaves page falls back to role logic.
  { key: "leaves-my", label: "My Leaves (apply / cancel own)", group: "Main", teamlioPath: "", feature: true, parent: "leaves", leveled: true, defaultVisible: true, defaultLevel: "manage" },
  { key: "leaves-approvals", label: "Pending Approvals (approve / reject)", group: "Main", teamlioPath: "", feature: true, parent: "leaves", leveled: true, defaultVisible: false, defaultLevel: "manage" },
  { key: "leaves-team", label: "All Team Requests", group: "Main", teamlioPath: "", feature: true, parent: "leaves", leveled: true, defaultVisible: false, defaultLevel: "view" },
  { key: "leaves-holidays", label: "Holidays (add / edit / remove)", group: "Main", teamlioPath: "", feature: true, parent: "leaves", leveled: true, defaultVisible: true, defaultLevel: "view" },

  // Dashboard cards — individual tiles, gated per Access profile (Visible +
  // View/Manage). Each maps to a `data-chart` tile in Dashboard.tsx. When a
  // profile doesn't specify a card the role default applies (employees see the
  // four personal cards; admins/managers see the org cards).
  { key: "dash-quick-actions", label: "Quick Actions & Activities", group: "Main", teamlioPath: "", feature: true, parent: "dashboard" },
  { key: "dash-top-active", label: "Top Active Employees (Admin)", group: "Main", teamlioPath: "", feature: true, parent: "dashboard" },
  { key: "dash-top-completers", label: "Top Task Completers (Admin)", group: "Main", teamlioPath: "", feature: true, parent: "dashboard" },
  { key: "dash-weekly-productivity", label: "Weekly Productivity / Daily Presence", group: "Main", teamlioPath: "", feature: true, parent: "dashboard" },
  { key: "dash-monthly-presence", label: "Monthly Presence Calendar", group: "Main", teamlioPath: "", feature: true, parent: "dashboard" },
  { key: "dash-weekly-task-flow", label: "Weekly Task Flow", group: "Main", teamlioPath: "", feature: true, parent: "dashboard" },
  { key: "dash-project-execution", label: "Project Execution Progress", group: "Main", teamlioPath: "", feature: true, parent: "dashboard" },
  { key: "dash-monthly-productivity", label: "Monthly Productivity", group: "Main", teamlioPath: "", feature: true, parent: "dashboard" },
  { key: "dash-tasks-priority", label: "Tasks by Priority", group: "Main", teamlioPath: "", feature: true, parent: "dashboard" },
  { key: "dash-tasks-status", label: "Tasks Health (Status Mix)", group: "Main", teamlioPath: "", feature: true, parent: "dashboard" },
  { key: "dash-leaves-pipeline", label: "Leaves Pipeline", group: "Main", teamlioPath: "", feature: true, parent: "dashboard" },
  { key: "dash-workforce", label: "Workforce", group: "Main", teamlioPath: "", feature: true, parent: "dashboard" },
  { key: "dash-projects", label: "Projects (Dashboard)", group: "Main", teamlioPath: "", feature: true, parent: "dashboard" },
  { key: "dash-attendance-health", label: "Attendance Health", group: "Main", teamlioPath: "", feature: true, parent: "dashboard" },
  { key: "dash-org-pulse", label: "Organisation Pulse", group: "Main", teamlioPath: "", feature: true, parent: "dashboard" },
];

export const PANEL_GROUPS: string[] = PANEL_CATALOG.reduce<string[]>((acc, p) => {
  if (!acc.includes(p.group)) acc.push(p.group);
  return acc;
}, []);

// Match a pathname to a panel key. Longest teamlioPath wins so "/tasks/reports"
// resolves to task-reports rather than the broader "tasks".
export function panelKeyForPath(pathname: string): string | null {
  const sorted = [...PANEL_CATALOG].sort(
    (a, b) => b.teamlioPath.length - a.teamlioPath.length
  );
  for (const p of sorted) {
    if (!p.teamlioPath) continue; // feature sub-permission — not a routed page
    if (pathname === p.teamlioPath || pathname.startsWith(p.teamlioPath + "/")) {
      return p.key;
    }
  }
  return null;
}

// Sidebar entries can point at a panel's landing route while the current page
// is a nested detail/history route. Compare the resolved panel keys so the
// correct child remains highlighted without broad string-prefix collisions.
export function panelPathIsActive(pathname: string, href: string): boolean {
  if (pathname === href) return true;
  const hrefKey = panelKeyForPath(href);
  return hrefKey !== null && panelKeyForPath(pathname) === hrefKey;
}

export function panelByKey(key: string): PanelDef | undefined {
  return PANEL_CATALOG.find((p) => p.key === key);
}
